import Papa from 'papaparse';
import type { SKU, MetricRow, Hueco, AppData } from './types';

const toBool = (v: unknown): boolean => {
  if (!v) return false;
  const s = String(v).trim().toUpperCase();
  return s === 'VERDADERO' || s === 'TRUE' || s === 'SI' || s === 'SÍ';
};

async function fetchText(path: string): Promise<string> {
  const res = await fetch(path);
  if (!res.ok) throw new Error(`Failed to fetch ${path}: ${res.status}`);
  return res.text();
}

function parseCSV<T>(text: string): T[] {
  const result = Papa.parse<T>(text, {
    header: true,
    delimiter: ';',
    skipEmptyLines: 'greedy',
    transformHeader: (h) => h.trim(),
  });
  if (result.errors.length) {
    console.warn('[csv] parse warnings:', result.errors.slice(0, 5));
  }
  return result.data;
}

export async function loadSKUs(): Promise<SKU[]> {
  const txt = await fetchText('./data/Portafolio-SKUs-Indigo.csv');
  const raw = parseCSV<Record<string, string>>(txt);
  return raw
    .filter((r) => r.SKU && r.SKU.trim())
    .map((r) => ({
      SKU: (r.SKU || '').trim(),
      Plataforma: (r.Plataforma || '').trim(),
      Suite: (r.Suite || '').trim(),
      Producto: (r.Producto || '').trim(),
      Modulo: (r.Modulo || '').trim(),
      BTIU: (r.BTIU || '').trim(),
      Descripcion: (r['Descripción'] || '').trim(),
      ListoDesarrollo: toBool(r['Listo Desarrollo']),
      ListaOferta: toBool(r['Lista Oferta']),
      ListoContrato: toBool(r['Listo Contrato']),
      ListoSLA: toBool(r['Listo SLA/Soporte']),
      Ventajas: (r.Ventajas || '').trim(),
      Riesgos: (r.Riesgos || '').trim(),
      Herramientas: (r.Herramientas || '').trim(),
      Pricing: (r.Pricing || '').trim(),
      Prioridad: (r.Prioridad || '').trim(),
      EsfuerzoEstimado: (r.Esfuerzo_Estimado || '').trim(),
      Dependencias: (r.Dependencias || '').trim(),
    }));
}

export async function loadMetrics(): Promise<MetricRow[]> {
  const txt = await fetchText('./data/Inventario-Metricas-Pulse.csv');
  const raw = parseCSV<Record<string, string>>(txt);
  return raw
    .filter((r) => {
      const cat = (r.Category || '').trim();
      const prod = (r.Product || '').trim();
      return cat && prod && (cat === 'Asistenciales' || cat === 'Administrativos');
    })
    .map((r) => ({
      Category: (r.Category || '').trim(),
      VieSuite: (r['Vie Suite'] || '').trim(),
      VieProduct: (r['Vie Product'] || '').trim(),
      Product: (r.Product || '').trim(),
      Module: (r.Module || '').trim(),
      DashboardType: (r['Dashboard Type'] || '').trim(),
      KPIDescription: (r['KPI Description'] || '').trim(),
      FichaTecnica: (r['Ficha Técnica'] || '').trim(),
      Accion: (r['Acción'] || '').trim(),
      TiempoData: (r['Tiempo Data'] || '').trim(),
      CheckData: (r['Check Data'] || '').trim(),
      Observaciones: (r.Observaciones || '').trim(),
      TiempoBI: (r['Tiempo BI'] || '').trim(),
      CheckBI: (r['check BI'] || '').trim(),
      OwnerCargo: (r.Owner_Cargo || '').trim(),
    }));
}

function inferEstado(text: string): 'resuelto' | 'parcial' | 'abierto' {
  if (/✅|RESUELTO|CERRAD/i.test(text)) return 'resuelto';
  if (/🟡|PARCIAL/i.test(text)) return 'parcial';
  return 'abierto';
}

export async function loadHuecos(): Promise<Hueco[]> {
  const txt = await fetchText('./data/huecos-argumentales.md');
  const lines = txt.split('\n');
  const huecos: Hueco[] = [];
  let currentBloque = '';
  const itemRe = /^###\s+([A-Z])\.(\d+)\s+(.+)$/;
  const bloqueRe = /^##\s+Bloque\s+\d+\s+—\s+(.+)$/i;
  let buffer: string[] = [];
  let current: { bloque: string; id: string; titulo: string } | null = null;

  const flush = () => {
    if (!current) return;
    const body = buffer.join('\n');
    huecos.push({
      bloque: current.bloque,
      id: current.id,
      titulo: current.titulo,
      estado: inferEstado(current.titulo + ' ' + body.slice(0, 500)),
      decision: body.trim().slice(0, 1200),
    });
    buffer = [];
  };

  for (const ln of lines) {
    const b = ln.match(bloqueRe);
    if (b) {
      flush();
      current = null;
      currentBloque = b[1].trim();
      continue;
    }
    const m = ln.match(itemRe);
    if (m) {
      flush();
      current = {
        bloque: currentBloque,
        id: `${m[1]}.${m[2]}`,
        titulo: m[3].trim(),
      };
      continue;
    }
    if (current) buffer.push(ln);
  }
  flush();
  return huecos;
}

export async function loadAll(): Promise<AppData> {
  const [skus, metrics, huecos] = await Promise.all([
    loadSKUs(),
    loadMetrics(),
    loadHuecos().catch((e) => {
      console.warn('[huecos] failed:', e);
      return [] as Hueco[];
    }),
  ]);
  return { skus, metrics, huecos, loadedAt: new Date() };
}
